/**
 * SuperAdmin Centre Insights - "Patients paid" card and Payments section. READ-ONLY.
 * GET /api/superadmin/centre-insights/payments/totals  and  /payments
 * The totals load in their own non-blocking call (usePaymentTotals), so a failure shows "-" on the card only. The
 * Payments section loads only while it is mounted: one call per open, type chip, page or period change. Stale responses
 * are ignored. Session and top-up rows open the existing request panel through onOpenRequest; package rows are plain text.
 * The shared styles and helpers (S, money, day, errText, Line, LinkLine, Card) come from CentreInsights.js via the ui prop.
 */

import React, { useState, useEffect } from 'react';
import api from '../../utils/api';

const BASE = '/superadmin/centre-insights';
const TYPE_CHIPS = [['all', 'All'], ['session', 'Sessions'], ['package', 'Packages'], ['topup', 'Top-ups']];
const KIND_LABEL = { session: 'Session', package: 'Package', topup: 'Top-up' };
const words = v => String(v || '-').replace(/_/g, ' ');

/** Totals for the card. Idle for All centres; any failure leaves data null so the card shows "-". */
export function usePaymentTotals(tenantId, period, errText) {
  const [state, setState] = useState({ loading: false, error: '', data: null });
  useEffect(() => {
    if (tenantId === 'all') return undefined;
    let current = true; // false after cleanup, so a stale response is ignored
    setState({ loading: true, error: '', data: null });
    (async () => {
      try {
        const res = await api.get(`${BASE}/payments/totals`, { params: { tenantId, period } });
        if (current) setState({ loading: false, error: '', data: res.data.data });
      } catch (e) { if (current) setState({ loading: false, error: errText(e), data: null }); }
    })();
    return () => { current = false; };
  }, [tenantId, period, errText]);
  return state;
}

export function PatientsPaidCard({ ui, totals, onOpen }) {
  const { Card, Line, LinkLine, money } = ui;
  const d = totals.data;
  const show = part => (d ? `${money(part.amountRupees)} (${part.count})` : '-');
  return (
    <Card title="Patients paid" note="Only paid and confirmed payments are counted.">
      <Line label="Total" value={d ? money(d.totalRupees) : '-'} />
      <LinkLine label="Sessions" what="payments" value={show(d ? d.sessions : null)} onClick={() => onOpen('session')} />
      <LinkLine label="Packages" what="payments" value={show(d ? d.packages : null)} onClick={() => onOpen('package')} />
      <LinkLine label="Top-ups" what="payments" value={show(d ? d.topUps : null)} onClick={() => onOpen('topup')} />
      {d && d.missingAmountCount > 0 && <div style={ui.S.note}>{d.missingAmountCount} without amount</div>}
    </Card>
  );
}

export function PaymentsSection({ ui, tenantId, period, initialType, onClose, onOpenRequest }) {
  const { S, money, day, errText } = ui;
  const [type, setType] = useState(initialType);
  const [paging, setPaging] = useState({ scope: '', n: 1 });
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ loading: true, error: '', rows: [], meta: null });

  // The period applies to every chip. The page goes back to 1 whenever the scope (centre, type or period) changes.
  const scope = `${tenantId}|${type}|${period}`;
  const page = paging.scope === scope ? paging.n : 1;

  useEffect(() => {
    let current = true; // false after cleanup, so a stale or unmounted response is ignored
    setState({ loading: true, error: '', rows: [], meta: null });
    (async () => {
      try {
        const res = await api.get(`${BASE}/payments`, { params: { tenantId, type, period, page } });
        if (current) setState({ loading: false, error: '', rows: res.data.data, meta: res.data.meta });
      } catch (e) { if (current) setState({ loading: false, error: errText(e), rows: [], meta: null }); }
    })();
    return () => { current = false; };
  }, [tenantId, type, period, page, attempt, errText]);

  const { loading, error, rows, meta } = state;
  const goTo = n => setPaging({ scope, n });
  return (
    <div style={{ marginTop: '16px' }}>
      <div style={{ ...S.row, justifyContent: 'space-between' }}>
        <h3 style={{ fontSize: '16px', margin: 0 }}>Payments</h3>
        <button type="button" style={S.btn} onClick={onClose}>Close</button>
      </div>
      <div style={S.row}>
        {TYPE_CHIPS.map(([v, l]) => <button key={v} type="button" style={S.chip(type === v)} onClick={() => setType(v)}>{l}</button>)}
      </div>
      {meta && <div style={{ ...S.row, fontSize: '14px', fontWeight: 600 }}>
        Total: {money(meta.totalAmountRupees)} ({meta.total} {meta.total === 1 ? 'row' : 'rows'})</div>}
      {error && <div style={S.error}>{error} <button type="button" style={S.btn} onClick={() => setAttempt(a => a + 1)}>Retry</button></div>}
      {loading && <p>Loading...</p>}
      {!loading && !error && rows.length === 0 && <p style={S.note}>No payments for this filter</p>}
      {rows.length > 0 && <div style={S.scroll}><table style={{ ...S.table, minWidth: '820px' }}>
        <thead><tr>{['Date', 'Patient', 'Type', 'Amount', 'Method', 'Status'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
        <tbody>{rows.map(r => (
          <tr key={r.id}>
            <td style={S.td}>{day(r.date)}</td>
            <td style={S.td}>{r.requestId
              ? <button type="button" style={S.nameBtn} onClick={() => onOpenRequest(r.requestId)}>{r.patientName}</button>
              : r.patientName}</td>
            <td style={S.td}>{KIND_LABEL[r.kind] || '-'}</td>
            <td style={S.td}>{r.amountMissing ? 'Amount missing' : money(r.amountRupees)}</td>
            <td style={S.td}>{words(r.paymentMethod)}</td>
            <td style={S.td}>{r.status === 'confirmed' ? 'Confirmed' : 'Paid'}</td>
          </tr>))}</tbody>
      </table></div>}
      <div style={S.note}>Opening this list is recorded in the audit log.</div>
      <div style={S.note}>Only paid and confirmed payments are counted. Amounts missing in the record are not counted.</div>
      <div style={S.note}>Package refunds cannot be detected, so refunded packages are still counted.</div>
      <div style={S.note}>Sessions paid from a package are counted under Packages.</div>
      {meta && meta.totalPages > 1 && <div style={{ ...S.row, marginTop: '10px' }}>
        <button type="button" style={S.btn} disabled={page <= 1} onClick={() => goTo(page - 1)}>Previous</button>
        <span style={{ fontSize: '14px' }}>Page {meta.page} of {meta.totalPages}</span>
        <button type="button" style={S.btn} disabled={page >= meta.totalPages} onClick={() => goTo(page + 1)}>Next</button>
      </div>}
    </div>
  );
}
